import { Row, Col, Card, CardBody } from "reactstrap"
import { useState } from "react"
import StatCard from "components/Common/StatCard"
import "./FuelUsageSummary.css"

// Short, human-readable date used across the summary cards.
const formatDate = dateStr => {
  if (!dateStr) return "-"
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

// Compact icon + label + value line used inside the period cards.
const ValueItem = ({ icon, label, value, className = "", tooltip }) => (
  <div
    className={`d-flex align-items-center gap-2 small ${className}`}
    title={tooltip}
  >
    <i className={`bx ${icon} text-muted`} />
    <span className="text-muted">{label}:</span>
    <span className="fw-medium">{value}</span>
  </div>
)

// Build the growth badge shared by every period/range card.
const formatGrowth = (growthData, currentData, previousData, periodType, comparisonText) => {
  if (!growthData || growthData.fuel_qty === null) return null

  const value = growthData.fuel_qty
  const trend = growthData.trend
  const currentQty = currentData?.total_fuel_qty || 0
  const previousQty = previousData?.total_fuel_qty || 0
  const tooltip =
    previousQty === 0
      ? `New ${periodType.toLowerCase()} data - no previous records`
      : `${comparisonText}: ${previousQty.toLocaleString()} L → ${currentQty.toLocaleString()} L`

  return {
    value: `${value > 0 ? "+" : ""}${value}%`,
    color: trend === "up" ? "success" : trend === "down" ? "danger" : "secondary",
    tooltip,
  }
}

// Inventory headline — total stock, fuel used and remaining fuel.
const TotalStats = ({ totals = {} }) => {
  const totalFuelQty = parseFloat(totals.total_fuel_qty ?? 0)
  const totalFuelStock = parseFloat(totals.total_fuel_stock ?? 0)
  const remainingFuelStock = parseFloat(totals.remaining_fuel_stock ?? 0)

  // Surface a low-stock warning when little fuel remains in the tank.
  const usedRatio = totalFuelStock > 0 ? totalFuelQty / totalFuelStock : 0
  const remainingColor =
    remainingFuelStock <= 0 ? "danger" : usedRatio >= 0.85 ? "warning" : "success"

  const stats = [
    {
      title: "Total Fuel Stock",
      value: `${totalFuelStock.toLocaleString()} L`,
      subtitle: "Total fuel purchased into stock",
      icon: "bx-cylinder",
      color: "info",
    },
    {
      title: "Fuel Used",
      value: `${totalFuelQty.toLocaleString()} L`,
      subtitle: "Fuel consumed by vehicles",
      icon: "bx-gas-pump",
      color: "warning",
    },
    {
      title: "Remaining Fuel",
      value: `${remainingFuelStock.toLocaleString()} L`,
      subtitle:
        remainingColor === "danger"
          ? "Stock depleted — purchase fuel"
          : remainingColor === "warning"
            ? "Running low — plan a purchase"
            : "Current fuel remaining in stock",
      icon: "bx-droplet",
      color: remainingColor,
    },
  ]

  return (
    <Row className="g-2 mb-4">
      {stats.map((stat, key) => (
        <Col md="4" key={"total_" + key}>
          <StatCard {...stat} />
        </Col>
      ))}
    </Row>
  )
}

// Single period/range card — one shared layout for "Today", "This Week",
// the selected date range, etc.
const PeriodCard = ({ title, icon, color = "primary", data = {}, comparison }) => {
  const fuelQty = parseFloat(data?.total_fuel_qty ?? 0)
  const trend = formatGrowth(
    data?.growth,
    data,
    data?.previous_period,
    title,
    comparison
  )

  return (
    <Card className="border-0 rounded-3 shadow-sm hover-lift transition-all h-100">
      <CardBody className="p-3">
        <div className="d-flex align-items-center justify-content-between mb-2">
          <h6 className="text-muted mb-0 small fw-medium">{title}</h6>
          <div
            style={{ width: "2rem", height: "2rem" }}
            className={`d-flex align-items-center justify-content-center rounded-circle p-1 bg-${color}-subtle`}
          >
            <i className={`bx ${icon} text-${color} fs-6`} />
          </div>
        </div>

        <div className="d-flex align-items-baseline justify-content-between mb-2">
          <h4 className="mb-0 fw-bold">{fuelQty.toLocaleString()} L</h4>
          {trend && (
            <span
              className={`badge bg-${trend.color}-subtle text-${trend.color} small`}
              title={trend.tooltip}
            >
              {trend.value}
            </span>
          )}
        </div>

        <div className="space-y-1">
          <ValueItem
            icon="bx-calendar"
            label="Period"
            value={`${formatDate(data?.from)} - ${formatDate(data?.to)}`}
            tooltip="Date range for this period"
          />

          {data?.previous_period && (
            <div className="pt-1 border-top">
              <ValueItem
                icon="bx-history"
                label="Previous"
                value={`${data.previous_period.total_fuel_qty?.toLocaleString() || 0} L`}
                tooltip={`Previous period: ${formatDate(data.previous_period.from)} to ${formatDate(data.previous_period.to)}`}
              />
            </div>
          )}

          {comparison && (
            <div className="pt-1">
              <div className="text-muted small" title={comparison}>
                <i className="bx bx-info-circle me-1" />
                {comparison}
              </div>
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  )
}

// Single card summarising the selected date range.
const RangeStats = ({ range = {}, comparison = "Compared to preceding period" }) => (
  <Row className="g-2">
    <Col md="4">
      <PeriodCard
        title={`Selected Range${
          range?.days ? ` (${range.days} day${range.days > 1 ? "s" : ""})` : ""
        }`}
        icon="bx-calendar-check"
        color="primary"
        data={range}
        comparison={comparison}
      />
    </Col>
  </Row>
)

// Fixed-period cards: Today / This Week / This Month / This Year.
const PeriodStats = ({ summary = {}, comparison_basis = {} }) => {
  const periodConfigs = [
    {
      title: "Today",
      icon: "bx-calendar",
      data: summary.today,
      color: "warning",
      comparison: comparison_basis?.today || "Compared to yesterday",
    },
    {
      title: "This Week",
      icon: "bx-calendar-week",
      data: summary.weekly,
      color: "info",
      comparison: comparison_basis?.weekly || "Compared to previous week",
    },
    {
      title: "This Month",
      icon: "bx-calendar",
      data: summary.monthly,
      color: "primary",
      comparison: comparison_basis?.monthly || "Compared to previous month",
    },
    {
      title: "This Year",
      icon: "bx-calendar-alt",
      data: summary.yearly,
      color: "success",
      comparison: comparison_basis?.yearly || "Compared to previous year",
    },
  ]

  return (
    <Row className="g-2">
      {periodConfigs.map((period, key) => (
        <Col md="3" key={"period_" + key}>
          <PeriodCard
            title={period.title}
            icon={period.icon}
            color={period.color}
            data={period.data || {}}
            comparison={period.comparison}
          />
        </Col>
      ))}
    </Row>
  )
}

// Main summary panel — collapsible inventory + period analytics.
const FuelUsageSummary = ({ totals = {}, summary = {}, comparison_basis = {} }) => {
  const [show, setShow] = useState(false)

  return (
    <>
      <div className="mb-2 d-flex justify-content-end">
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={() => setShow(prev => !prev)}
        >
          {show ? "Hide Summary ▲" : "Show Summary ▼"}
        </button>
      </div>
      {show && (
        <>
          <TotalStats totals={totals} />
          {summary?.mode === "range" ? (
            <RangeStats
              range={summary.range}
              comparison={comparison_basis?.range}
            />
          ) : (
            <PeriodStats summary={summary} comparison_basis={comparison_basis} />
          )}
        </>
      )}
    </>
  )
}

export default FuelUsageSummary
